import React from "react";
import { IcCheck, IcDoc, IcExternal, IcSend } from "./shared.jsx";
import { api, idempotencyKey } from "../lib/api.js";

const { useState: a_uS } = React;

const ADRE_LOOKUP_URL = "https://services.azre.gov/PdbWeb/IndividualLicense/SearchIndividualLicenses";

// formType:
//   "pdf"     — BoldSign workflow; sign in-app, we email on completion
//   "online"  — association uses an online portal; we link out
const ASSOC_OPTIONS = [
  { k: "phx",        name: "Phoenix REALTORS",                        short: "PAR",    url: "phoenixrealtors.com",     region: "Phoenix",
    formType: "online", formUrl: "https://www.phoenixrealtors.com/forms/member-or-office-change-form/", portalLabel: "PAR Member Change Form" },
  { k: "saar",       name: "Scottsdale Area Association of REALTORS", short: "SAAR",   url: "scottsdalerealtors.org",  region: "Scottsdale",
    formType: "online", formUrl: "https://saar.formstack.com/forms/transfer_form_2",    portalLabel: "SAAR Transfer Form" },
  { k: "weserv",     name: "WeSERV",                                  short: "WeSERV", url: "weserv.realtor",          region: "West & SE Valley",
    formType: "online", formUrl: "https://weserv.wufoo.com/forms/zemnvez0r6ojgk/",     portalLabel: "WeSERV Member Transfer" },
  { k: "tar",        name: "Tucson Association of REALTORS",          short: "TAR",    url: "tucsonrealtors.org",      region: "Tucson",
    formType: "pdf",    formUrl: "https://www.tucsonrealtors.org/wp-content/uploads/Notification-of-Transfer-Form.pdf" },
  { k: "paar",       name: "Prescott Area Association of REALTORS",   short: "PAAR",   url: "paar.org",                region: "Prescott",
    formType: "online", formUrl: "https://members.paar.org/ap/Membership/Application/mP72o9pZ", portalLabel: "PAAR Membership Application" },
  { k: "naar",       name: "Northern Arizona Assn. of REALTORS",      short: "NAAR",   url: "nazrealtor.com",          region: "Flagstaff",
    formType: "pdf",    formUrl: "https://static1.squarespace.com/static/63da49326f16db751561ac83/t/66e1e27456b2e040817d4370/1726079604543/NAMLS+Member+Change+Form.pdf" },
];

const PDF_ASSOCS    = ASSOC_OPTIONS.filter(a => a.formType === "pdf");
const ONLINE_ASSOCS = ASSOC_OPTIONS.filter(a => a.formType === "online");

// ============ MAIN PANEL ============
function AssociationsPanel({ associations, setAssociations, agent }) {
  const assocSel   = associations?.assoc || [];
  const formStatus = associations?.formStatus || {};
  const [sendErr, setSendErr] = a_uS({});
  const [adreReady, setAdreReady] = a_uS({});

  const toggle = (k) => {
    const curr = associations.assoc || [];
    const next = curr.includes(k) ? curr.filter(x => x !== k) : [...curr, k];
    setAssociations({ ...associations, assoc: next });
  };

  const setStatus = (k, status) => {
    setAssociations(curr => ({
      ...curr,
      formStatus: { ...(curr.formStatus || {}), [`assoc:${k}`]: status },
    }));
  };

  // When an agent opens an online portal, mark it opened and auto-select the
  // association so the listing transfer step knows which board to use.
  function openPortal(a) {
    setStatus(a.k, "opened");
    if (!(associations.assoc || []).includes(a.k)) {
      setAssociations(curr => ({
        ...curr,
        assoc: [...(curr.assoc || []), a.k],
      }));
    }
  }

  async function signMemberForm(a) {
    setStatus(a.k, "sending");
    setSendErr(curr => ({ ...curr, [a.k]: null }));
    try {
      const res = await api.post("/v1/associations/transfer", { association: a.short },
        { headers: { "Idempotency-Key": idempotencyKey() } });
      if (res.signUrl) window.open(res.signUrl, "_blank", "noopener");
      setStatus(a.k, "signed");
    } catch (e) {
      setStatus(a.k, undefined);
      setSendErr(curr => ({ ...curr, [a.k]: e.message || "Could not send the form" }));
    }
  }

  const selectedPdf = PDF_ASSOCS.filter(a => assocSel.includes(a.k));

  const AssocCheckbox = ({ a }) => {
    const sel = assocSel.includes(a.k);
    return (
      <label style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "11px 14px", borderRadius: 2, cursor: "pointer",
        border: `1px solid ${sel ? "var(--ep-sunset)" : "var(--ep-border)"}`,
        background: sel ? "#FFFAF0" : "var(--ep-bg-elevated)",
        transition: "border-color 120ms var(--ep-ease), background 120ms var(--ep-ease)",
      }}>
        <div style={{
          width: 16, height: 16, borderRadius: 2, flexShrink: 0,
          border: "1.5px solid var(--ep-origin)",
          background: sel ? "var(--ep-sunset)" : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--ep-saguaro-blossom)",
        }}>
          {sel && <IcCheck size={10}/>}
        </div>
        <input type="checkbox" checked={sel} onChange={() => toggle(a.k)} style={{ display: "none" }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ep-origin)", lineHeight: 1.3 }}>{a.name}</div>
          <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 1 }}>{a.region}</div>
        </div>
      </label>
    );
  };

  return (
    <div>
      {/* ── SELECTOR — PDF associations only (BoldSign workflow) ── */}
      <div style={{ marginBottom: selectedPdf.length ? 36 : 8 }}>
        <div className="ep-eyebrow" style={{ marginBottom: 20 }}>
          Which associations do you belong to?
        </div>
        <div className="vstack" style={{ gap: 24 }}>
          {/* PDF associations — select to trigger BoldSign sign flow */}
          <div>
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
              paddingBottom: 8, borderBottom: "1px solid var(--ep-border)",
            }}>
              <IcDoc size={13} style={{ color: "var(--ep-sunset)", flexShrink: 0 }}/>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ep-origin)" }}>
                Sign here — we send the form via BoldSign
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              {PDF_ASSOCS.map(a => <AssocCheckbox key={a.k} a={a} />)}
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION A: PDF → BoldSign ── */}
      {selectedPdf.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)", marginBottom: 14 }}>Sign here</div>
          <div className="vstack" style={{ gap: 10 }}>
            {selectedPdf.map(a => {
              const status = formStatus[`assoc:${a.k}`];
              const done   = status === "signed" || status === "confirmed";
              return (
                <div key={a.k} className="ep-card" style={{
                  padding: "18px 22px",
                  display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
                  borderColor: done ? "var(--ep-avocado)" : "var(--ep-border)",
                  background: done ? "rgba(204,207,129,0.1)" : "var(--ep-bg-elevated)",
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                    background: done ? "var(--ep-avocado)" : "var(--ep-origin-10)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--ep-origin)",
                  }}>
                    {done ? <IcCheck size={20}/> : <IcDoc size={20}/>}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: "300 20px/1.2 var(--ep-font-display)", letterSpacing: "-0.005em" }}>
                      {a.name}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ep-fg-muted)", marginTop: 2 }}>
                      Member change form · Pre-filled with your license and contact info
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {done ? (
                        <span className="ep-badge signed"><IcCheck size={10}/> Submitted</span>
                      ) : status === "sending" ? (
                        <span className="ep-badge sent">Preparing…</span>
                      ) : (
                        <>
                          <a href={a.formUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                            <button className="ep-btn ghost sm">View PDF <IcExternal size={10}/></button>
                          </a>
                          <button
                            className="ep-btn primary sm"
                            onClick={() => signMemberForm(a)}
                            disabled={a.k === "tar" && !adreReady[a.k]}
                            style={a.k === "tar" && !adreReady[a.k] ? { opacity: 0.45, cursor: "not-allowed" } : {}}>
                            Sign &amp; Submit <IcSend size={11}/>
                          </button>
                        </>
                      )}
                    </div>
                    {sendErr[a.k] && (
                      <span style={{ color: "var(--ep-rosewood)", fontSize: 11, maxWidth: 240, textAlign: "right" }}>{sendErr[a.k]}</span>
                    )}
                  </div>

                  {/* TAR-specific: ADRE screenshot preflight */}
                  {a.k === "tar" && !done && (
                    <div style={{
                      width: "100%", marginTop: 14, padding: "14px 16px",
                      background: "rgba(196,98,45,0.07)", borderRadius: 2,
                      borderLeft: "3px solid var(--ep-sunset)",
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ep-origin)", marginBottom: 6 }}>
                        TAR requires an ADRE screenshot with this form
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", lineHeight: 1.6, marginBottom: 10 }}>
                        Before signing, go to ADRE and confirm EveryPlace Real Estate shows as your designated broker.
                        Take a screenshot — you'll upload it inside the BoldSign signing window.
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <a href={ADRE_LOOKUP_URL} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                          <button className="ep-btn ghost sm" style={{ fontSize: 11 }}>
                            Open ADRE Lookup <IcExternal size={10}/>
                          </button>
                        </a>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: "var(--ep-origin)" }}>
                          <div style={{
                            width: 16, height: 16, borderRadius: 2, flexShrink: 0,
                            border: "1.5px solid var(--ep-origin)",
                            background: adreReady[a.k] ? "var(--ep-sunset)" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "var(--ep-saguaro-blossom)",
                          }}>
                            {adreReady[a.k] && <IcCheck size={10}/>}
                          </div>
                          <input type="checkbox" checked={!!adreReady[a.k]}
                            onChange={e => setAdreReady(curr => ({ ...curr, [a.k]: e.target.checked }))}
                            style={{ display: "none" }}/>
                          I have my ADRE screenshot ready to upload
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SECTION B: Online portals — always visible, no checkbox required ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
          paddingBottom: 8, borderBottom: "1px solid var(--ep-border)",
        }}>
          <IcExternal size={13} style={{ color: "var(--ep-sunset)", flexShrink: 0 }}/>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ep-origin)" }}>
            Online portals — submit directly on the association website
          </span>
        </div>
        <div className="ep-card" style={{ padding: 0, overflow: "hidden" }}>
          {ONLINE_ASSOCS.map((a, i) => {
            const status = formStatus[`assoc:${a.k}`];
            const done   = status === "opened" || status === "confirmed";
            return (
              <div key={a.k} style={{
                padding: "16px 22px",
                borderBottom: i < ONLINE_ASSOCS.length - 1 ? "1px solid var(--ep-divider)" : 0,
                background: done ? "rgba(204,207,129,0.08)" : "transparent",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: "500 14px/1.3 var(--ep-font-body)", color: "var(--ep-origin)" }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 2 }}>{a.region}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {done && <span className="ep-badge signed"><IcCheck size={10}/> Opened</span>}
                    <a href={a.formUrl} target="_blank" rel="noreferrer"
                      style={{ textDecoration: "none" }}
                      onClick={() => openPortal(a)}>
                      <button className="ep-btn primary sm">
                        Open {a.short} Form <IcExternal size={10}/>
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { AssociationsPanel, ASSOC_OPTIONS };
